// Header.tsx

import React, { useState, useEffect, useContext, useRef } from 'react';
import { FaRegUser } from 'react-icons/fa';
import { FiShoppingCart } from 'react-icons/fi';
import { BiWallet } from 'react-icons/bi';
import { SlBadge } from 'react-icons/sl';
import { MdOutlineLogout } from 'react-icons/md';
import { IoSettingsOutline } from 'react-icons/io5';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { UserContext } from '../UserContext';
import Cookies from 'js-cookie';
import { IoMenu, IoClose } from 'react-icons/io5';
import { Categories } from '../../data';
import { createPortal } from 'react-dom';
import i18n from '../../language/i18n';
import LoginPopup from '../auth/LoginPopup';

interface CustomTooltipProps {
  children: React.ReactNode;
  title: string;
  isSidebarCollapsed: boolean;
}

const CustomTooltip = ({ children, title, isSidebarCollapsed }: CustomTooltipProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const elementRef = useRef<HTMLDivElement>(null);

  const updateTooltipPosition = () => {
    if (elementRef.current) {
      const rect = elementRef.current.getBoundingClientRect();
      setTooltipPosition({
        top: rect.top + rect.height / 2,
        left: rect.right + 3
      });
    }
  };

  useEffect(() => {
    updateTooltipPosition();
    
    // 윈도우 리사이즈 이벤트 리스너 추가
    window.addEventListener('resize', updateTooltipPosition);
    
    // 사이드바 트랜지션이 완료된 후 위치 업데이트
    const transitionTimeout = setTimeout(updateTooltipPosition, 300);

    return () => {
      window.removeEventListener('resize', updateTooltipPosition);
      clearTimeout(transitionTimeout);
    };
  }, [isSidebarCollapsed]);

  return (
    <div 
      className="relative"
      ref={elementRef}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {children}
      {isSidebarCollapsed && isHovered && (
        createPortal(
          <div 
            className="fixed bg-gray-800 text-white px-2 py-1 rounded text-sm whitespace-nowrap z-[99999999]"
            style={{
              top: tooltipPosition.top,
              left: tooltipPosition.left,
              transform: 'translateY(-50%)'
            }}
          >
            {title}
            <div className="absolute top-1/2 -left-1 -translate-y-1/2 border-4 border-transparent border-r-gray-800"/>
          </div>,
          document.body
        )
      )}
    </div>
  );
};

const userOptions = ['로그인', '회원가입'];

// 카테고리 이름과 이미지 파일명 매핑
const categoryIconMap: { [key: string]: string } = {
  '모델링': 'modeling',
  '픽셀': 'pixel',
  '플러그인': 'plugin',
  '스크립트': 'script',
  '스킬': 'skill',
  '스킨': 'skin',
  '건축': 'build',
  '엔티티': 'entity',
  '무료': 'free'
};

// 상단에 baseURL 상수 추가
const BASE_URL = 'https://cnitsstudio-api.com:570';

export default function Header() {
  const { t } = useTranslation();
  const { user, setUser } = useContext(UserContext);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [username, setUsername] = useState('');
  const [useremail, setuseremail] = useState('test@test.com');
  const [userprofile, setuserprofile] = useState('');
  const [roles, setRoles] = useState<string[]>([]);
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isPointOpen, setIsPointOpen] = useState(false); // 수정: 올바른 상태 업데이트 함수 사용
  const [isBrandSubMenu, setIsBrandSubMenu] = useState(false);
  const [mobileMenuCategory, setmobileMenuCategory] = useState(false);
  const [mobileMenuBrand, setmobileMenuBrand] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 767);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [showSignupPopup, setShowSignupPopup] = useState(false);

  // user 상태가 변경될 때마다 로그인 상태와 사용자 정보를 업데이트
  useEffect(() => {
    if (user) {
      setIsLoggedIn(true);
      setUsername(user.username);
      // profileImageUrl이 전체 URL이 아닌 경우에만 BASE_URL과 결합
      const fullProfileUrl = user.profileImageUrl?.startsWith('http') 
        ? user.profileImageUrl 
        : `${BASE_URL}${user.profileImageUrl}`;
      setuserprofile(fullProfileUrl);
      setuseremail(user.email);
      setRoles(user.roles);
    } else {
      setIsLoggedIn(false);
      setUsername('');
      setRoles([]);
    }
  }, [user]); // user가 변경될 때마다 실행

  // 컴포넌트 마운트 시 로그인 상태 확인
  useEffect(() => {
    const accessToken = Cookies.get('access_token');
    const storedUser = localStorage.getItem('user');

    if (accessToken && storedUser) {
      const userData = JSON.parse(storedUser);
      setUser(userData); // UserContext 업데이트
    }
  }, [setUser]);

  // localStorage에서 직접 가져오는 대신 context의 user 사용
  const nicknameToDisplay = user?.nickname || username;

  // 로그인 팝업 관련 핸들러들
  const handleLoginClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowLoginPopup(true);
  };

  const handleCloseLoginPopup = () => {
    setShowLoginPopup(false);
  };

  const handleSwitchToSignup = () => {
    setShowLoginPopup(false);
    navigate('/signup'); // 또는 회원가입 팝업을 표시하는 로직으로 변경
  };

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  useEffect(() => {
    // 로그인 상태나 사용자 정보 로드 로직을 추가할 수 있습니다.
    const storedUsername = Cookies.get('username');
    if (storedUsername) {
      setUsername(storedUsername);
      setIsLoggedIn(true);
    }
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 767);

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const mobilemenutoggleMenu = () => {
    setmobileMenuCategory(!mobileMenuCategory);
  };

  const handleCategoryClick = () => {
    // toggleMenu() 호출 제거
    // setmobileMenuCategory(false) 호출 제거
    // 아무 동작도 하지 않음
  };

  const mobilemenutoggleBrand = () => {
    setmobileMenuBrand(!mobileMenuBrand);
  };

  const handleBrandClick = () => {
    toggleMenu(); // isMobileOpen 상태를 false로 변경
    setmobileMenuBrand(false); // mobileMenuCategory를 닫힌 상태로 설정
  };

  const handleMouseEnter = () => {
    setIsBrandSubMenu(true);
  };

  const handleMouseLeave = () => {
    setIsBrandSubMenu(false);
  };

  const toggleMenu = () => {
    setIsMobileOpen(!isMobileOpen);
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 767) {
        setIsMobileOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);

    // 컴포넌트가 언마운트 될 때 이벤트 리스너 제거
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  let timeoutId: ReturnType<typeof setTimeout>;

  const navigate = useNavigate();
  const location = useLocation(); // 현재 위치를 추적합니다

  // 광고 표시 여부 상태 변수
  const [isAdVisible, setIsAdVisible] = useState(true);

  useEffect(() => {
    // 쿠키에서 access_token과 user 정보를 확인
    const accessToken = Cookies.get('access_token');
    const user = localStorage.getItem('user'); // 사용자 정보를 여전히 localStorage에 저장할 경우

    if (accessToken && user) {
      const userData = JSON.parse(user);
      setIsLoggedIn(true);
      setUsername(userData.username);
      setuserprofile(userData.profileImageUrl);
      setuseremail(userData.email);
      setRoles(userData.roles); // 역할 저장
    } else {
      setIsLoggedIn(false);
      setUsername('');
      setRoles([]); // 역할 초기화
    }

    // 광고 관련 로직은 그대로 유지
    const dismissedDate = localStorage.getItem('adDismissedDate');
    const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD

    if (dismissedDate === today) {
      setIsAdVisible(false);
    } else {
      setIsAdVisible(true);
    }
  }, []);

  // Language 드롭다운 열기/닫기
  const toggleLanguageDropdown = () => {
    setIsLanguageDropdownOpen((prev) => !prev);
    setIsUserDropdownOpen(false); // 사용자 드롭다운 닫기
    setIsPointOpen(false); // 포인트 드롭다운 닫기
  };

  // User 드롭다운 열기/닫기
  const toggleUserDropdown = () => {
    setIsUserDropdownOpen((prev) => !prev);
    setIsLanguageDropdownOpen(false); // 언어 드롭다운 닫기
    setIsPointOpen(false); // 포인트 드롭다운 닫기
  };

  // Point 드롭다운 열기/닫기
  const togglePointDropdown = () => {
    setIsPointOpen((prev) => !prev);
    setIsLanguageDropdownOpen(false); // 언어 드롭다운 닫기
    setIsUserDropdownOpen(false); // 사용자 드롭다운 닫기
  };

  const handleLogout = () => {
    // 1. 쿠키에서 인증 정보 제거
    Cookies.remove('access_token', { path: '/' });
    Cookies.remove('refresh_token', { path: '/' });
    Cookies.remove('user', { path: '/' }); // 사용자 정보가 쿠키에 저장되어 있다면
  
    // 2. 로컬 스토리지에서 사용자 정보 제거 (필요 시)
    localStorage.removeItem('auth'); // 'auth'가 여전히 로컬 스토리지에 존재한다면
    localStorage.removeItem('user'); // 'user' 정보도 로컬 스토리지에 저장되어 있다면
  
    // 3. 사용자 상태 초기화
    setUser(null); // UserContext의 사용자 상태 초기화
    setIsLoggedIn(false); // 로그인 상태 초기화
    setUsername(''); // 사용자 이름 초기화
  
    // 4. 기타 UI 업데이트
    toast.success(t('header.logout_success'));
    setIsUserDropdownOpen(false); // 사용자 드롭다운 닫기
    setIsMobileOpen(false); // 사이드 메뉴 닫기
  
    // 5. 페이지 리디렉트
    navigate('/'); // 로그아웃 후 홈으로 이동
  };

  useEffect(() => {
    const handleClickOutside = (event: Event) => {
      setIsLanguageDropdownOpen(false);
      setIsUserDropdownOpen(false);
    };

    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    // 페이지 위치가 변경될 때 드롭다운을 닫습니다
    setIsLanguageDropdownOpen(false);
    setIsUserDropdownOpen(false);
    setIsDropdownVisible(false);
  }, [location]); // location이 변경될 때마다 호출됩니다

  const handleDropdownClick = (event: React.MouseEvent<HTMLLIElement>) => {
    event.stopPropagation();
  };

  const handleUserOptionClick = (option: string) => {
    if (option === '로그인') {
      navigate('/login');
    } else if (option === '회원가입') {
      navigate('/signup');
    }
    setIsUserDropdownOpen(false);
  };

  const showDropdown = () => {
    clearTimeout(timeoutId);
    setIsDropdownVisible(true);
  };

  const hideDropdown = () => {
    timeoutId = setTimeout(() => {
      setIsDropdownVisible(false);
    }, 200); // 200ms delay
  };

  const handleClick = () => {
    navigate('/notice');
  };

  // 광고 제 함수
  const handleDismissAd = () => {
    const today = new Date().toISOString().slice(0, 10); // Get YYYY-MM-DD
    localStorage.setItem('adDismissedDate', today);
    setIsAdVisible(false);
  };

  // Helper 함수: USER가 아닌 역할이 있는지 확인
  const hasNonUserRole = () => {
    return roles.some((role) => role !== 'USER');
  };

  return (
    <>
      {/* Left Sidebar */}
      <div className={`fixed left-0 top-0 h-screen bg-white border-r border-gray-200 z-50 transition-all duration-300 flex flex-col ${
        isSidebarCollapsed ? 'w-[60px]' : 'w-[250px]'
      }`}>
        {/* Sidebar Header with Logo and Toggle */}
        <div className="p-4 border-b flex items-center justify-between">
          {/* Logo */}
          <div className={`${isSidebarCollapsed ? 'hidden' : 'block'}`}>
            <NavLink to="/">
              <img 
                src="/logo/logo.png" 
                alt="Logo" 
                className="h-8"
              />
            </NavLink>
          </div>
          
          {/* Toggle Button */}
          <button 
            onClick={toggleSidebar}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            {isSidebarCollapsed ? <IoMenu /> : <IoClose />}
          </button>
        </div>

        {/* Sidebar Content - Add flex-1 and flex flex-col to create proper layout */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Main menu items - Add overflow-y-auto */}
          <div className="flex-1 overflow-y-auto">
            <ul className="p-3 space-y-2">
              <li>
                <CustomTooltip title={t('header.home')} isSidebarCollapsed={isSidebarCollapsed}>
                  <NavLink to="/" className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50">
                    <span className="text-gray-600">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                        <path fill="currentColor" d="M10 20v-6h4v6h5v-8h3L12 3L2 12h3v8z"/>
                      </svg>
                    </span>
                    {!isSidebarCollapsed && <span>{t('header.home')}</span>}
                  </NavLink>
                </CustomTooltip>
              </li>

              <li>
                <CustomTooltip title={t('header.category')} isSidebarCollapsed={isSidebarCollapsed}>
                  <div 
                    className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer"
                    onClick={mobilemenutoggleMenu}
                  >
                    <span className="text-gray-600">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                        <path fill="currentColor" d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z"/>
                      </svg>
                    </span>
                    {!isSidebarCollapsed && (
                      <div className="flex items-center justify-between flex-1">
                        <span>{t('header.category')}</span>
                        <span className="text-sm">
                          {mobileMenuCategory ? <IoClose /> : <IoMenu />}
                        </span>
                      </div>
                    )}
                  </div>
                </CustomTooltip>
                {/* 카테고리 서브메뉴 */}
                {!isSidebarCollapsed && (
                  <div 
                    className={`overflow-hidden transition-all duration-300 ease-in-out ${
                      mobileMenuCategory ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <ul className="pl-6 mt-2 space-y-4">
                      {Categories.map((category) => (
                        <div key={category.name} className="category-column">
                          <NavLink 
                            to={category.path}
                          >
                            <div className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-100">
                              <img 
                                src={`/icon/${categoryIconMap[category.name]}.png`}
                                alt={category.name}
                                className="w-5 h-5 object-contain"
                              />
                              <h3 className="text-sm font-semibold text-gray-700">
                                {t(`categories.${category.name}`)}
                              </h3>
                            </div>
                          
                          </NavLink>
                        </div>
                      ))}
                    </ul>
                  </div>
                )}
              </li>

              {/* <li>
                <CustomTooltip title={t('header.brand')} isSidebarCollapsed={isSidebarCollapsed}>
                  <div 
                    className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer"
                    onClick={mobilemenutoggleBrand}
                  >
                    <span className="text-gray-600">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                        <path fill="currentColor" d="M12 2L2 7v10l10 5l10-5V7z"/>
                      </svg>
                    </span>
                    {!isSidebarCollapsed && (
                      <div className="flex items-center justify-between flex-1">
                        <span>{t('header.brand')}</span>
                        <span className="text-sm">
                          {mobileMenuBrand ? <IoClose /> : <IoMenu />}
                        </span>
                      </div>
                    )}
                  </div>
                </CustomTooltip>
  
                {!isSidebarCollapsed && (
                  <div 
                    className={`overflow-hidden transition-all duration-300 ease-in-out ${
                      mobileMenuBrand ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <ul className="pl-6 mt-2 space-y-2">
                      <li onClick={handleBrandClick}>
                        <NavLink
                          to="/brandzone/halloween"
                          className="text-sm text-gray-600 hover:text-gray-900"
                        >
                          Halloween
                        </NavLink>
                      </li>
  
                    </ul>
                  </div>
                )}
              </li> */}

              <li>
                <CustomTooltip title={t('header.event')} isSidebarCollapsed={isSidebarCollapsed}>
                  <NavLink to="/event" className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50">
                    <span className="text-gray-600">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                        <path fill="currentColor" d="M21 6h-2V5c0-.55-.45-1-1-1h-6c-.55 0-1 .45-1 1v1H9V5c0-.55-.45-1-1-1H2c-.55 0-1 .45-1 1v1H0v2h1v11c0 .55.45 1 1 1h7c.55 0 1-.45 1-1V8h2v11c0 .55.45 1 1 1h7c.55 0 1-.45 1-1V8h1V6z"/>
                      </svg>
                    </span>
                    {!isSidebarCollapsed && <span>{t('header.event')}</span>}
                  </NavLink>
                </CustomTooltip>
              </li>

              <li>
                <CustomTooltip title={t('header.support')} isSidebarCollapsed={isSidebarCollapsed}>
                  <div 
                    className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer"
                    onClick={() => {
                      handleClick();
                      toggleMenu();
                    }}
                  >
                    <span className="text-gray-600">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                        <path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10s10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41c0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z"/>
                      </svg>
                    </span>
                    {!isSidebarCollapsed && <span>{t('header.support')}</span>}
                  </div>
                </CustomTooltip>
              </li>

              <li>
                <CustomTooltip title={t('header.projects')} isSidebarCollapsed={isSidebarCollapsed}>
                  <NavLink to="/projects" className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50">
                    <span className="text-gray-600">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                        <path fill="currentColor" d="M3 3h18v18H3V3zm16 16V5H5v14h14zM7 7h10v2H7V7zm0 4h10v2H7v-2zm0 4h7v2H7v-2z"/>
                      </svg>
                    </span>
                    {!isSidebarCollapsed && <span>{t('header.projects')}</span>}
                  </NavLink>
                </CustomTooltip>
              </li>
            </ul>
          </div>

          {/* Bottom section - Add mt-auto to push it to bottom */}
          <div className="border-t p-3 space-y-2 mt-auto bg-white relative">
            {/* Language Selector */}
            <CustomTooltip title={t('header.language')} isSidebarCollapsed={isSidebarCollapsed}>
              <div 
                className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer relative"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleLanguageDropdown();
                }}
              >
                <span className="text-gray-600">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                    <path fill="currentColor" d="M12.87 15.07l-2.54-2.51l.03-.03A17.52 17.52 0 0 0 14.07 6H17V4h-7V2H8v2H1v2h11.17C11.5 7.92 10.44 9.75 9 11.35C8.07 10.32 7.3 9.19 6.69 8h-2c.73 1.63 1.73 3.17 2.98 4.56l-5.09 5.02L4 19l5-5l3.11 3.11l.76-2.04zM18.5 10h-2L12 22h2l1.12-3h4.75L21 22h2l-4.5-12zm-2.62 7l1.62-4.33L19.12 17h-3.24z"/>
                  </svg>
                </span>
                {!isSidebarCollapsed && (
                  <div className="flex items-center justify-between flex-1">
                    <span>{t('header.language')}</span>
                  </div>
                )}
              </div>
            </CustomTooltip>

            {/* Language Dropdown */}
            {isLanguageDropdownOpen && !isSidebarCollapsed && (
              <div className="absolute left-0 bottom-full mb-1 w-full bg-white border rounded-lg shadow-lg py-2 z-50">
                <div 
                  className="px-4 py-2 hover:bg-gray-50 cursor-pointer"
                  onClick={() => {
                    i18n.changeLanguage('ko');
                    toggleLanguageDropdown();
                  }}
                >
                  한국어
                </div>
                <div 
                  className="px-4 py-2 hover:bg-gray-50 cursor-pointer"
                  onClick={() => {
                    i18n.changeLanguage('en');
                    toggleLanguageDropdown();
                  }}
                >
                  English
                </div>
                <div 
                  className="px-4 py-2 hover:bg-gray-50 cursor-pointer"
                  onClick={() => {
                    i18n.changeLanguage('jp');
                    toggleLanguageDropdown();
                  }}
                >
                  日本語
                </div>
              </div>
            )}

            {/* Collapsed sidebar language dropdown */}
            {isLanguageDropdownOpen && isSidebarCollapsed && createPortal(
              <div 
                className="fixed bg-white border rounded-lg shadow-lg py-2 z-[99999999]"
                style={{
                  left: '70px',
                  bottom: '120px',
                  minWidth: '120px'
                }}
              >
                <div 
                  className="px-4 py-2 hover:bg-gray-50 cursor-pointer"
                  onClick={() => {
                    i18n.changeLanguage('ko');
                    toggleLanguageDropdown();
                  }}
                >
                  한국어
                </div>
                <div 
                  className="px-4 py-2 hover:bg-gray-50 cursor-pointer"
                  onClick={() => {
                    i18n.changeLanguage('en');
                    toggleLanguageDropdown();
                  }}
                >
                  English
                </div>
                <div 
                  className="px-4 py-2 hover:bg-gray-50 cursor-pointer"
                  onClick={() => {
                    i18n.changeLanguage('ja');
                    toggleLanguageDropdown();
                  }}
                >
                  日本語
                </div>
              </div>,
              document.body
            )}

            {/* User Profile Section - Show different content based on login status */}
            <CustomTooltip title={isLoggedIn ? t('header.profile') : t('header.login')} isSidebarCollapsed={isSidebarCollapsed}>
              <div 
                className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer relative"
                onClick={(e) => {
                  e.stopPropagation();
                  if (isLoggedIn) {
                    toggleUserDropdown();
                  } else {
                    handleLoginClick(e);
                  }
                }}
              >
                <span className="text-gray-600">
                  {isLoggedIn && userprofile ? (
                    <img 
                      src={userprofile}
                      alt="Profile"
                      className="w-6 h-6 rounded-full object-cover"
                    />
                  ) : (
                    <FaRegUser size={24} />
                  )}
                </span>
                {!isSidebarCollapsed && (
                  <div className="flex items-center justify-between flex-1">
                    <span>{isLoggedIn ? nicknameToDisplay : t('header.login')}</span>
                  </div>
                )}
              </div>
            </CustomTooltip>

            {/* Profile Dropdown Menu */}
            {isUserDropdownOpen && !isSidebarCollapsed && (
              <div className="absolute left-0 bottom-full mb-1 w-full bg-white border rounded-lg shadow-lg py-2 z-50">
                <NavLink to="/profile" className="px-4 py-2 hover:bg-gray-50 flex items-center">
                  <FaRegUser className="mr-2" /> {t('header.profile')}
                </NavLink>
                <NavLink to="/purchases" className="px-4 py-2 hover:bg-gray-50 flex items-center">
                  <FiShoppingCart className="mr-2" /> {t('header.purchase_history')}
                </NavLink>
                <NavLink to="/emblem" className="px-4 py-2 hover:bg-gray-50 flex items-center">
                  <SlBadge className="mr-2" /> {t('header.emblem')}
                </NavLink>
                <NavLink to="/cart" className="px-4 py-2 hover:bg-gray-50 flex items-center">
                  <FiShoppingCart className="mr-2" /> {t('product_card.add_to_cart')}
                </NavLink>
                <NavLink to="/settings" className="px-4 py-2 hover:bg-gray-50 flex items-center">
                  <IoSettingsOutline className="mr-2" /> {t('header.settings')}
                </NavLink>

              </div>
            )}

            {/* Collapsed sidebar profile dropdown */}
            {isUserDropdownOpen && isSidebarCollapsed && createPortal(
              <div 
                className="fixed bg-white border rounded-lg shadow-lg py-2 z-[99999999]"
                style={{
                  left: '70px',
                  bottom: '120px',
                  minWidth: '120px'
                }}
              >
                <NavLink to="/profile" className="px-4 py-2 hover:bg-gray-50 flex items-center">
                  <FaRegUser className="mr-2" /> {t('header.profile')}
                </NavLink>
                <NavLink to="/purchases" className="px-4 py-2 hover:bg-gray-50 flex items-center">
                  <FiShoppingCart className="mr-2" /> {t('header.purchase_history')}
                </NavLink>
                <NavLink to="/emblem" className="px-4 py-2 hover:bg-gray-50 flex items-center">
                  <SlBadge className="mr-2" /> {t('header.emblem')}
                </NavLink>
                <NavLink to="/cart" className="px-4 py-2 hover:bg-gray-50 flex items-center">
                  <FiShoppingCart className="mr-2" /> {t('product_card.add_to_cart')}
                </NavLink>
                <NavLink to="/settings" className="px-4 py-2 hover:bg-gray-50 flex items-center">
                  <IoSettingsOutline className="mr-2" /> {t('header.settings')}
                </NavLink>
              </div>,
              document.body
            )}

            {/* Logout Button - Only show when logged in */}
            {isLoggedIn && (
              <CustomTooltip title={t('header.logout')} isSidebarCollapsed={isSidebarCollapsed}>
                <div 
                  className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer"
                  onClick={handleLogout}
                >
                  <span className="text-gray-600">
                    <MdOutlineLogout size={24} />
                  </span>
                  {!isSidebarCollapsed && <span>{t('header.logout')}</span>}
                </div>
              </CustomTooltip>
            )}
          </div>
        </div>
      </div>

      {/* Main Content Area - adjust margin to account for sidebar */}
      <div className={`transition-all duration-300 ${
        isSidebarCollapsed ? 'ml-[60px]' : 'ml-[250px]'
      }`}>
        {isAdVisible && (
          <div className={`fixed top-0 right-0 bg-black text-white h-8 mobile:h-[60px] flex justify-between items-center z-[2000] transition-all duration-300 ${
            isSidebarCollapsed ? 'left-[60px]' : 'left-[250px]'
          }`}>
            <div className="flex-1 px-8 mobile:px-4">
              <p
                className="text-xs mobile:text-[10px] Pretendard-Regular text-nowrap tracking-wide text-white flex"
                dangerouslySetInnerHTML={{
                  __html:
                    window.innerWidth <= 767
                      ? t('header.mobile_ad_content')
                      : t('header.ad_content'),
                }}
              />
            </div>
            <button
              onClick={handleDismissAd}
              className="px-8 mobile:px-4 text-xs Pretendard-Regular mobile:text-[10px] text-nowrap tracking-wide hover:underline whitespace-nowrap"
            >
              {t('header.dismiss_ad')}
            </button>
          </div>
        )}

        {/* 로그인 팝업 */}
        {showLoginPopup && (
          <LoginPopup
            onClose={handleCloseLoginPopup}
            onSwitchToSignup={handleSwitchToSignup}
          />
        )}
      </div>
    </>
  );
}
